'use client';

import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { usePostHog } from 'posthog-js/react';
import { Button } from '@/components/shared/Button';
import { fadeInUp, staggerContainer } from '@/lib/animations';
import type { AmbassadorContent } from '@/lib/content-schema';

interface Props {
  content: AmbassadorContent['cta'];
}

export function AmbassadorCTA({ content }: Props) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const posthog = usePostHog();

  return (
    <section className="py-24 md:py-32 bg-gradient-to-br from-zibarit-verde-oscuro to-zibarit-gris-oscuro">
      <motion.div
        ref={ref}
        variants={staggerContainer}
        initial="initial"
        animate={isInView ? 'animate' : 'initial'}
        className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
      >
        <motion.p
          variants={fadeInUp}
          className="text-body text-base md:text-lg text-zibarit-bone/70 italic mb-6"
        >
          {content.subtitle}
        </motion.p>

        <motion.h2
          variants={fadeInUp}
          className="text-title text-3xl md:text-4xl lg:text-5xl text-zibarit-bone mb-10"
        >
          {content.titleBefore}
          <span className="text-zibarit-verde-brillante">{content.titleAccent}</span>
        </motion.h2>

        {content.buttonVisible && (
          <motion.div variants={fadeInUp}>
            <Button
              href="#aplicar"
              magnetic
              size="lg"
              data-ph-capture
              onClick={() => posthog?.capture('landing_cta_clicked', { button_text: content.buttonText, destination_url: '#aplicar', page: 'ambassadors' })}
            >
              {content.buttonText}
            </Button>
            <p className="text-body text-sm text-zibarit-bone/60 mt-6">
              {content.buttonNote}
            </p>
          </motion.div>
        )}
      </motion.div>
    </section>
  );
}
